<script setup lang="ts">
import imgClose from '@/assets/Fill 1@2x.png'
import ModalButtonSpinner from '@/components/ModalButtonSpinner.vue'

defineProps<{
  visible: boolean
  message: string
  submitting: boolean
}>()

const emit = defineEmits<{
  close: []
  confirm: []
}>()
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="cancel-schedule-modal-root"
      role="presentation"
    >
      <div
        class="cancel-schedule-modal schedule-confirm-modal schedule-unassign-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unassign-schedule-title"
      >
        <header class="cancel-schedule-modal-head">
          <h2 id="unassign-schedule-title">Unassign Schedule</h2>
          <button
            type="button"
            class="cancel-schedule-modal-x"
            aria-label="Close"
            :disabled="submitting"
            @click="emit('close')"
          >
            <img :src="imgClose" class="cancel-schedule-modal-x-ic" alt="" />
          </button>
        </header>
        <div class="cancel-schedule-modal-body">
          <p class="cancel-schedule-modal-text">{{ message }}</p>
        </div>
        <footer class="cancel-schedule-modal-foot">
          <button
            type="button"
            class="cancel-schedule-btn cancel-schedule-btn-outline"
            :disabled="submitting"
            @click="emit('close')"
          >
            <span class="schedule-confirm-no-label">No</span>
          </button>
          <button
            type="button"
            class="cancel-schedule-btn cancel-schedule-btn-solid"
            :disabled="submitting"
            @click="emit('confirm')"
          >
            <span
              class="hm-modal-btn-content"
              :class="{ 'hm-modal-btn-content--loading': submitting }"
            >
              <span class="schedule-confirm-yes-label">Yes</span>
              <ModalButtonSpinner v-if="submitting" />
            </span>
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cancel-schedule-modal-root {
  position: fixed;
  inset: 0;
  z-index: 12700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.35);
}

.cancel-schedule-modal {
  width: min(520px, 100%);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.schedule-confirm-modal.cancel-schedule-modal {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 530px;
  min-width: 530px;
  max-width: min(530px, calc(100vw - 48px));
  background: #ffffff;
  border: 1px solid #d8d8d8;
  border-radius: 16px;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.schedule-unassign-modal.schedule-confirm-modal {
  height: 232px;
  min-height: 232px;
  max-height: 232px;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-modal-head {
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  min-height: 48px;
  max-height: 48px;
  margin: 0;
  padding: 0;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-modal-head h2 {
  position: absolute;
  top: 24px;
  left: 24px;
  box-sizing: border-box;
  width: 149px;
  min-width: 149px;
  max-width: 149px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  margin: 0;
  padding: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 16px;
  font-weight: bold;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  white-space: nowrap;
  overflow: visible;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-modal-x {
  position: absolute;
  top: 28px;
  right: 32px;
  left: auto;
  z-index: 1;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-modal-body {
  flex: 0 0 auto;
  box-sizing: border-box;
  margin: 0;
  padding: 24px 24px 0;
  overflow: visible;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-modal-text {
  display: block;
  box-sizing: border-box;
  width: 482px;
  min-width: 482px;
  max-width: 482px;
  height: 80px;
  min-height: 80px;
  max-height: 80px;
  margin: 0;
  padding: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
}

.schedule-confirm-modal .cancel-schedule-modal-x {
  position: absolute;
  top: 28px;
  right: 32px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 0;
}

.schedule-confirm-modal .cancel-schedule-modal-x-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  display: block;
  object-fit: contain;
}

.schedule-confirm-modal .cancel-schedule-modal-foot {
  position: absolute;
  inset: 0;
  flex: none;
  box-sizing: border-box;
  min-height: 0;
  margin: 0;
  padding: 0;
  pointer-events: none;
}

.schedule-confirm-modal .cancel-schedule-btn {
  position: absolute;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  margin: 0;
  cursor: pointer;
  pointer-events: auto;
}

.schedule-confirm-modal .schedule-confirm-no-label {
  display: block;
  box-sizing: border-box;
  width: 19px;
  min-width: 19px;
  max-width: 19px;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  margin: 0;
  padding: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: right;
  white-space: nowrap;
}

.schedule-confirm-modal .schedule-confirm-yes-label {
  display: block;
  box-sizing: border-box;
  width: 23px;
  min-width: 23px;
  max-width: 23px;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  margin: 0;
  padding: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: right;
  white-space: nowrap;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-btn-outline {
  top: 176px;
  left: 330px;
  bottom: auto;
  box-sizing: border-box;
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  border-radius: 16px;
  border: 1px solid #000000;
  background: #ffffff;
  justify-content: flex-end;
  padding: 6px 30px 6px 31px;
}

.schedule-confirm-modal .cancel-schedule-btn-solid {
  box-sizing: border-box;
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  border-radius: 16px;
  border: none;
  background: #000000;
  justify-content: flex-end;
  padding: 6px 28px 6px 29px;
}

.schedule-unassign-modal.schedule-confirm-modal .cancel-schedule-btn-solid {
  top: 176px;
  right: 24px;
  left: auto;
  bottom: auto;
}

.cancel-schedule-btn-outline {
  background: #fff;
  border: 1px solid #111;
  color: #111;
}

.cancel-schedule-btn-solid {
  background: #111;
  border: 1px solid #111;
  color: #fff;
}
</style>
